import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, Check, Search, ShieldCheck, KeyRound } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TitleCard from "../../../components/TitleCard/TitleCard";
import GradientButton from "../../../components/GradientButton/GradientButton";
import CancelButton from "../../../components/CancelButton/CancelButton";

const getResponseItems = (response) => response.data?.data || response.data || [];

const getItemId = (item) => item?._id?.toString?.() || item?.id?.toString?.() || item;

const UserAccessAssignment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userId } = useParams();
    const type = location.pathname.includes("/assign-permissions/")
        ? "permissions"
        : "roles";
    const isPermissionAssignment = type === "permissions";
    const userFromLocation = location.state?.user;
    const [user, setUser] = useState(userFromLocation);
    const [catalog, setCatalog] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    const [search, setSearch] = useState("");
    const [permissionPage, setPermissionPage] = useState(0);
    const [saved, setSaved] = useState(false);
    const permissionsPerPage = 10;

    useEffect(() => {
        const loadCatalog = async () => {
            setIsLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("access_token");
                const resource = type === "roles" ? "roles" : "permissions";
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/${resource}/get-all`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setCatalog(getResponseItems(response).map((item) => ({
                    id: getItemId(item),
                    name: item.name || item.code || "Sin nombre",
                    description: item.description || item.module || "Sin descripción",
                })));
            } catch (requestError) {
                console.error(`Error cargando ${type}:`, requestError);
                setError(`No fue posible cargar ${isPermissionAssignment ? "los permisos" : "los roles"} disponibles.`);
            } finally {
                setIsLoading(false);
            }
        };

        loadCatalog();
    }, [isPermissionAssignment, type]);

    useEffect(() => {
        if (type !== "roles") return;
        const currentItems = user?.[type] || [];
        setSelectedItems(currentItems.map(getItemId).filter(Boolean));
    }, [type, user]);

    useEffect(() => {
        if (userFromLocation) return;

        const loadUser = async () => {
            try {
                const token = localStorage.getItem("access_token");
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/users/get-by-id/${userId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setUser(response.data?.data || response.data);
            } catch (requestError) {
                console.error("Error cargando usuario:", requestError);
                setError("No fue posible cargar la información del usuario.");
            }
        };

        loadUser();
    }, [userId, userFromLocation]);

    const filteredCatalog = useMemo(() => {
        const normalizedSearch = search.toLowerCase().trim();
        if (!normalizedSearch) return catalog;

        return catalog.filter((item) =>
            `${item.name} ${item.description}`.toLowerCase().includes(normalizedSearch)
        );
    }, [catalog, search]);

    const totalPermissionPages = Math.ceil(filteredCatalog.length / permissionsPerPage);
    const visibleCatalog = useMemo(() => {
        if (!isPermissionAssignment) return filteredCatalog;
        const start = permissionPage * permissionsPerPage;
        return filteredCatalog.slice(start, start + permissionsPerPage);
    }, [filteredCatalog, isPermissionAssignment, permissionPage]);

    useEffect(() => {
        setPermissionPage(0);
    }, [search]);

    useEffect(() => {
        if (totalPermissionPages > 0 && permissionPage >= totalPermissionPages) {
            setPermissionPage(totalPermissionPages - 1);
        }
    }, [permissionPage, totalPermissionPages]);

    const toggleItem = (itemId) => {
        setSaved(false);
        setSelectedItems((current) =>
            current.includes(itemId)
                ? current.filter((id) => id !== itemId)
                : [...current, itemId]
        );
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError("");
        try {
            const token = localStorage.getItem("access_token");
            const field = type === "roles" ? "roles" : "permissions";
            await axios.put(
                `${process.env.REACT_APP_API_URL}/users/update/${userId}`,
                { [field]: selectedItems },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSaved(true);
        } catch (requestError) {
            console.error(`Error asignando ${type}:`, requestError);
            setError(
                requestError.response?.data?.message ||
                `No fue posible guardar ${isPermissionAssignment ? "los permisos" : "los roles"} del usuario.`
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <TitleCard title={isPermissionAssignment ? "Asignar permisos" : "Asignar roles"} />

            <div className="flex items-center justify-between gap-4 my-4">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-700"
                >
                    <ArrowLeft size={18} />
                    Volver a usuarios
                </button>
                <span className="text-sm text-gray-500">
                    {selectedItems.length} {isPermissionAssignment ? "permisos" : "roles"} seleccionados
                </span>
            </div>

            <form onSubmit={handleSubmit} className="min-w-0 bg-white rounded-lg shadow-md p-5 text-gray-700">
                <div className="flex items-start gap-3 border-b pb-5 mb-5">
                    {isPermissionAssignment ? (
                        <KeyRound className="text-blue-600 mt-1" size={22} />
                    ) : (
                        <ShieldCheck className="text-blue-600 mt-1" size={22} />
                    )}
                    <div>
                        <h2 className="text-lg font-semibold">Usuario seleccionado</h2>
                        <p className="text-sm text-gray-600">
                            {user?.name || user?.username || `Usuario ${userId}`}
                            {user?.email ? ` - ${user.email}` : ""}
                        </p>
                    </div>
                </div>

                <div className="relative mb-4 max-w-md">
                    <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={`Buscar ${isPermissionAssignment ? "permisos" : "roles"}`}
                        className="w-full border border-gray-300 rounded px-10 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                </div>

                {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

                {isLoading ? (
                    <p className="py-8 text-center text-sm text-gray-500">
                        Cargando {isPermissionAssignment ? "permisos" : "roles"}...
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {visibleCatalog.map((item) => {
                            const isSelected = selectedItems.includes(item.id);
                            return (
                                <div
                                    key={item.id}
                                    role="checkbox"
                                    aria-checked={isSelected}
                                    tabIndex={0}
                                    onClick={() => toggleItem(item.id)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            toggleItem(item.id);
                                        }
                                    }}
                                    className={`flex min-w-0 items-start gap-3 border rounded-lg p-4 cursor-pointer transition select-none ${isSelected ? "border-blue-600 bg-blue-50" : "border-gray-200 hover:border-blue-300"
                                        }`}
                                >
                                    <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border ${isSelected ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300"
                                        }`}>
                                        {isSelected && <Check size={14} />}
                                    </span>
                                    <span className="min-w-0 break-words">
                                        <span className="block break-words font-medium">{item.name}</span>
                                        <span className="block break-words text-sm text-gray-500">{item.description}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!isLoading && isPermissionAssignment && totalPermissionPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <button
                            type="button"
                            disabled={permissionPage === 0}
                            onClick={() => setPermissionPage((page) => page - 1)}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50"
                        >
                            Anterior
                        </button>
                        <span className="text-sm text-gray-500">
                            Página {permissionPage + 1} de {totalPermissionPages}
                        </span>
                        <button
                            type="button"
                            disabled={permissionPage === totalPermissionPages - 1}
                            onClick={() => setPermissionPage((page) => page + 1)}
                            className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded disabled:opacity-50"
                        >
                            Siguiente
                        </button>
                    </div>
                )}

                {filteredCatalog.length === 0 && (
                    <p className="py-8 text-center text-sm text-gray-500">No se encontraron opciones.</p>
                )}

                <div className="flex flex-row flex-nowrap items-center gap-4 mt-6">
                    <GradientButton type="submit" disabled={isSaving || isLoading}>
                        {isSaving ? "Guardando..." : "Guardar"}
                    </GradientButton>
                    <CancelButton onClick={() => navigate(-1)} />
                    {saved && <span className="text-sm text-green-600">Asignaciones guardadas correctamente.</span>}
                </div>
            </form>
        </>
    );
};

export default UserAccessAssignment;